import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Spinner, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import ViewQuoteDetailModal from '../../components/ViewQuoteDetailModal';
import { PUBLIC_API_BASE_URL } from '../../lib/config';

const MyQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [legalFeeData, setLegalFeeData] = useState(null);
  const [confirmingQuote, setConfirmingQuote] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('customer_token');
      const response = await axios.get(`${PUBLIC_API_BASE_URL}/customer-portal/leads`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.status === 200) {
        setQuotes(response.data.data.leads);
      }
    } catch (err) {
      console.error('Error fetching quotes:', err);
      if (err.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('customer_token');
        localStorage.removeItem('customer_data');
        navigate('/customer-portal/login');
      } else {
        toast.error('Failed to load quotes');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewQuote = async (quote) => {
    try {
      const token = localStorage.getItem('customer_token');

      // Fetch full lead details
      const response = await axios.get(`${PUBLIC_API_BASE_URL}/customer-portal/lead/${quote.lead_id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.status === 200) {
        const leadData = response.data.data;
        setSelectedQuote(leadData);

        // Fetch legal fee data if address available
        if (leadData.address?.postcode) {
          try {
            const feeResponse = await axios.post(
              '/api/v1/admin/getLegalFeeByPostcode',
              {
                postcode: leadData.address.postcode,
                service_type: leadData.service_type,
                property_value: leadData.net_value || 0
              },
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );

            if (feeResponse.data) {
              setLegalFeeData(feeResponse.data);
            }
          } catch (feeErr) {
            console.error('Error fetching legal fees:', feeErr);
          }
        }

        setShowDetailModal(true);
      }
    } catch (err) {
      console.error('Error fetching quote details:', err);
      toast.error('Failed to load quote details');
    }
  };

  const handleInstructQuote = async (quoteId) => {
    const result = await Swal.fire({
      title: 'Instruct This Quote?',
      html: '<p>Are you sure you want to instruct this quote?</p><p>This will confirm your order and notify us to proceed.</p>',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#198754',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Instruct',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    setConfirmingQuote(quoteId);
    try {
      const token = localStorage.getItem('customer_token');
      const response = await axios.post(
        `${PUBLIC_API_BASE_URL}/customer-portal/lead/${quoteId}/confirm`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === 200) {
        toast.success('Quote instructed successfully! We will proceed with your order.');
        fetchQuotes(); // Refresh list
      }
    } catch (err) {
      console.error('Error instructing quote:', err);
      toast.error(err.response?.data?.message || 'Failed to instruct quote');
    } finally {
      setConfirmingQuote(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    return `£${Number(amount || 0).toFixed(2)}`;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>My Quotes</h2>
        <Button
          variant="outline-secondary"
          onClick={fetchQuotes}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise me-2"></i>
          Refresh
        </Button>
      </div>

      {loading ? (
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center py-5">
            <Spinner animation="border" style={{ color: '#764ba2' }} />
            <p className="mt-3 text-muted">Loading your quotes...</p>
          </Card.Body>
        </Card>
      ) : quotes.length === 0 ? (
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center py-5">
            <i className="bi bi-inbox" style={{ fontSize: '80px', color: '#ccc' }}></i>
            <h5 className="mt-3">No Quotes Found</h5>
            <p className="text-muted">You don't have any quotes yet.</p>
          </Card.Body>
        </Card>
      ) : (
        <Card className="shadow-sm border-0">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead style={{ background: '#f8f9fa' }}>
                  <tr>
                    <th>Lead ID</th>
                    <th>Service Type</th>
                    <th>Quote Amount</th>
                    <th>Date Created</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((quote) => (
                    <tr key={quote._id}>
                      <td className="fw-bold">{quote.lead_id}</td>
                      <td>
                        <Badge bg="secondary" className="px-3 py-2">
                          {quote.service_type || 'N/A'}
                        </Badge>
                      </td>
                      <td className="fw-bold" style={{ color: '#764ba2' }}>
                        {formatCurrency(quote.total_quote)}
                      </td>
                      <td>{formatDate(quote.createdAt)}</td>
                      <td>
                        {quote.customer_confirmed ? (
                          <Badge bg="success" className="px-3 py-2">
                            <i className="bi bi-check-circle me-1"></i>
                            Instructed
                          </Badge>
                        ) : (
                          <Badge bg="warning" text="dark" className="px-3 py-2">
                            <i className="bi bi-clock me-1"></i>
                            Quote Sent
                          </Badge>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            className="red-bg border-0"
                            onClick={() => navigate(`/customer-portal/quote/${quote.lead_id}`)}
                          >
                            <i className="bi bi-eye me-1"></i>
                            View Detail
                          </Button>
                          {!quote.customer_confirmed && (
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => handleInstructQuote(quote.lead_id)}
                              disabled={confirmingQuote === quote.lead_id}
                            >
                              {confirmingQuote === quote.lead_id ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <>
                                  <i className="bi bi-check-lg me-1"></i>
                                  Instruct
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Quote Detail Modal */}
      {selectedQuote && (
        <ViewQuoteDetailModal
          show={showDetailModal}
          onHide={() => {
            setShowDetailModal(false);
            setSelectedQuote(null);
            setLegalFeeData(null);
          }}
          leadId={selectedQuote.lead_id}
          legalFeeData={legalFeeData}
          leadData={selectedQuote}
        />
      )}
    </div>
  );
};

export default MyQuotes;
